import {
  type ResearchSummaryDetail,
  getResearchSummary,
  resetResearchSummary,
  updateResearchSummary
} from "./researchClient";
import Markdown from "@app/components/Markdown/Markdown";
import { Accordion, ActionIcon, Button, Card, Group, Stack, Text, Textarea, Tooltip } from "@mantine/core";
import { useEffect, useState } from "react";

interface Props {
  summaryId?: string;
  summary?: ResearchSummaryDetail;
  /** Render as an expandable Accordion.Item (must be wrapped in an Accordion). Defaults to a standalone Card. */
  collapsible?: boolean;
}

/** First non-empty line of the body, with common markdown markers stripped, for the collapsed header. */
function previewLine(markdown: string): string {
  const line =
    markdown
      .split("\n")
      .map(l => l.replace(/^[#>\-*\s]+/, "").trim())
      .find(Boolean) ?? "";
  return line;
}

export function ResearchSummaryMessage({ summaryId, summary: preloaded, collapsible }: Props) {
  const [summary, setSummary] = useState<ResearchSummaryDetail | null>(preloaded ?? null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(preloaded?.markdown_body ?? "");
  const [saving, setSaving] = useState(false);

  const effectiveId = summaryId ?? preloaded?.id;

  useEffect(() => {
    if (preloaded || !summaryId) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const fetched = await getResearchSummary(summaryId);
        if (!cancelled) {
          setSummary(fetched);
          setDraft(fetched.markdown_body);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [summaryId, preloaded]);

  async function handleSave() {
    if (!summary || !effectiveId) {
      return;
    }
    setSaving(true);
    try {
      const updated = await updateResearchSummary(effectiveId, { markdown_body: draft });
      setSummary(updated);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!summary || !effectiveId) {
      return;
    }
    try {
      const updated = await resetResearchSummary(effectiveId);
      setSummary(updated);
      setDraft(updated.markdown_body);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function handleCopy() {
    if (!summary) {
      return;
    }
    void navigator.clipboard.writeText(summary.markdown_body);
  }

  if (error) {
    return <Text c="red">Summary load failed: {error}</Text>;
  }
  if (!summary) {
    return <Text c="dimmed">Loading summary…</Text>;
  }

  const actions = (
    <Group gap="xs">
      {!editing && (
        <Button size="xs" variant="light" onClick={() => setEditing(true)}>
          Edit
        </Button>
      )}
      <Tooltip label="Reset to retrieval-agent output">
        <ActionIcon variant="subtle" onClick={() => void handleReset()}>
          ↺
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Copy markdown">
        <ActionIcon variant="subtle" onClick={handleCopy}>
          📋
        </ActionIcon>
      </Tooltip>
    </Group>
  );

  const body = editing ? (
    <Stack>
      <Textarea value={draft} onChange={e => setDraft(e.currentTarget.value)} minRows={12} autosize />
      <Group justify="flex-end">
        <Button
          size="xs"
          variant="subtle"
          onClick={() => {
            setDraft(summary.markdown_body);
            setEditing(false);
          }}
        >
          Cancel
        </Button>
        <Button size="xs" onClick={() => void handleSave()} loading={saving}>
          Save
        </Button>
      </Group>
    </Stack>
  ) : (
    <Markdown>{summary.markdown_body}</Markdown>
  );

  const sources = summary.sources.length > 0 && (
    <Stack mt="md" gap={4}>
      <Text size="xs" fw={500}>
        Sources
      </Text>
      {summary.sources.map(s => (
        <Text key={s.cite_id} size="xs">
          [{s.cite_id}] {s.authors} ({s.year}) — {s.title}
        </Text>
      ))}
    </Stack>
  );

  if (collapsible) {
    return (
      <Accordion.Item value={effectiveId ?? summary.title}>
        <Accordion.Control>
          <Text component="span" display="block" fw={600}>
            {summary.title}
          </Text>
          <Text component="span" display="block" size="xs" c="dimmed" lineClamp={1}>
            {previewLine(summary.markdown_body)}
          </Text>
        </Accordion.Control>
        <Accordion.Panel>
          <Group justify="flex-end" mb="xs">
            {actions}
          </Group>
          {body}
          {sources}
        </Accordion.Panel>
      </Accordion.Item>
    );
  }

  return (
    <Card withBorder>
      <Group justify="space-between" mb="xs">
        <Text fw={600}>{summary.title}</Text>
        {actions}
      </Group>
      {body}
      {sources}
    </Card>
  );
}
